import moment from "moment-timezone";

export const isValidSaitamaDateRange = (dateRange) => {
  if (!dateRange || !dateRange[0] || !dateRange[1]) return false;
  return !moment.tz(dateRange[0], "YYYY-MM-DD", "Asia/Tokyo").isAfter(
    moment.tz(dateRange[1], "YYYY-MM-DD", "Asia/Tokyo"),
    "day"
  );
};

export const toSaitamaUtcBounds = (dateRange, season) => {
  const selectedStart = moment.tz(`${dateRange[0]} 00:00:00`, "Asia/Tokyo");
  const selectedEnd = moment.tz(`${dateRange[1]} 00:00:00`, "Asia/Tokyo").add(1, "day");
  const seasonStart = moment.utc(season.seasonStartUtc);
  const seasonEnd = moment.utc(season.seasonEndUtc);
  return {
    startUtc: moment.max(selectedStart.utc(), seasonStart).format("YYYY-MM-DD HH:mm:ss"),
    endUtc: moment.min(selectedEnd.utc(), seasonEnd).format("YYYY-MM-DD HH:mm:ss"),
  };
};

const streetLightExists = (alias) => `
  EXISTS (
    SELECT 1
    FROM power_asset_detail_of_saitama_city sc
    JOIN street_light sl ON sl.id = sc.power_asset_id
    WHERE sc.power_asset_id = ${alias}.power_asset_id
  )`;

export const buildSaitamaQueries = ({ seasonId, startUtc, endUtc, grain }) => {
  const assetBucket = grain === "hour"
    ? "DATE_FORMAT(DATE_ADD(pae.occurred_datetime, INTERVAL 9 HOUR), '%Y-%m-%d %H:00')"
    : "DATE(DATE_ADD(pae.occurred_datetime, INTERVAL 9 HOUR))";
  const photographerBucket = grain === "hour"
    ? "DATE_FORMAT(DATE_ADD(pae.occurred_datetime, INTERVAL 9 HOUR), '%Y-%m-%d %H:00')"
    : "DATE(DATE_ADD(pae.occurred_datetime, INTERVAL 9 HOUR))";
  const query = (sql) => ({ query: sql, database: "KpiDashboard", type: "list" });

  return {
    kpi01: query(`SELECT COUNT(*) AS total_count
      FROM photo_of_power_asset ppa
      WHERE ppa.game_space_id = '${seasonId}'
        AND ${streetLightExists("ppa")}
        AND EXISTS (SELECT 1 FROM power_asset_event pae
          WHERE pae.game_space_id = ppa.game_space_id
            AND pae.power_asset_id = ppa.power_asset_id
            AND pae.player_id = ppa.player_id
            AND pae.type = 'PHOTOGRAPH'
            AND pae.occurred_datetime >= '${startUtc}'
            AND pae.occurred_datetime < '${endUtc}');`),
    kpi02: query(`SELECT COUNT(DISTINCT ppa.power_asset_id) AS total_asset_count
      FROM photo_of_power_asset ppa
      WHERE ppa.game_space_id = '${seasonId}'
        AND ${streetLightExists("ppa")}
        AND EXISTS (SELECT 1 FROM power_asset_event pae
          WHERE pae.game_space_id = ppa.game_space_id
            AND pae.power_asset_id = ppa.power_asset_id
            AND pae.player_id = ppa.player_id
            AND pae.type = 'PHOTOGRAPH'
            AND pae.occurred_datetime >= '${startUtc}'
            AND pae.occurred_datetime < '${endUtc}');`),
    kpi03: query(`SELECT COUNT(DISTINCT rr.power_asset_id) AS review_ok_asset_count
      FROM review_request_event rre
      JOIN review_request rr ON rr.id = rre.review_request_id
      WHERE rr.game_space_id = '${seasonId}'
        AND rre.type = 'APPROVED'
        AND rre.occurred_datetime >= '${startUtc}'
        AND rre.occurred_datetime < '${endUtc}'
        AND EXISTS (SELECT 1 FROM photo_of_power_asset ppa
          WHERE ppa.game_space_id = rr.game_space_id
            AND ppa.power_asset_id = rr.power_asset_id
            AND ${streetLightExists("ppa")});`),
    kpi05: query(`SELECT COUNT(DISTINCT rr.player_id) AS unique_photographer_count
      FROM review_request_event rre
      JOIN review_request rr ON rr.id = rre.review_request_id
      JOIN review_request_event_of_approved rreoa ON rreoa.id = rre.id
      WHERE rr.game_space_id = '${seasonId}'
        AND rre.type = 'APPROVED'
        AND rre.occurred_datetime >= '${startUtc}'
        AND rre.occurred_datetime < '${endUtc}'
        AND EXISTS (SELECT 1 FROM photo_of_power_asset ppa
          WHERE ppa.game_space_id = rr.game_space_id
            AND ppa.power_asset_id = rr.power_asset_id
            AND ${streetLightExists("ppa")});`),
    kpi06Denominator: query(`SELECT COUNT(DISTINCT paga.power_asset_id) AS registered_eligible_street_light_count
      FROM power_asset_game_space_area paga
      WHERE paga.game_space_id = '${seasonId}'
        AND EXISTS (SELECT 1 FROM power_asset_detail_of_saitama_city sc
          JOIN street_light sl ON sl.id = sc.power_asset_id
          WHERE sc.power_asset_id = paga.power_asset_id);`),
    chartAssets: query(`SELECT ${assetBucket} AS bucket,
        COUNT(DISTINCT pae.power_asset_id) AS photographed_asset_count
      FROM power_asset_event pae
      JOIN photo_of_power_asset ppa
        ON ppa.game_space_id = pae.game_space_id
       AND ppa.power_asset_id = pae.power_asset_id
       AND ppa.player_id = pae.player_id
      WHERE pae.game_space_id = '${seasonId}'
        AND pae.type = 'PHOTOGRAPH'
        AND pae.occurred_datetime >= '${startUtc}'
        AND pae.occurred_datetime < '${endUtc}'
        AND ${streetLightExists("ppa")}
      GROUP BY ${assetBucket} ORDER BY bucket ASC;`),
    chartPhotographers: query(`SELECT ${photographerBucket} AS bucket,
        COUNT(DISTINCT pae.player_id) AS unique_photographer_count
      FROM power_asset_event pae
      WHERE pae.game_space_id = '${seasonId}'
        AND pae.type = 'PHOTOGRAPH'
        AND pae.occurred_datetime >= '${startUtc}'
        AND pae.occurred_datetime < '${endUtc}'
        AND ${streetLightExists("pae")}
      GROUP BY ${photographerBucket} ORDER BY bucket ASC;`),
  };
};

export const normalizeRows = (response) => {
  if (Array.isArray(response)) return response;
  if (response && Array.isArray(response.body)) return response.body;
  if (response && typeof response.body === "string") {
    try { return JSON.parse(response.body); } catch (e) { return []; }
  }
  return [];
};

const firstValue = (rows, key) => rows[0] && rows[0][key] !== undefined ? Number(rows[0][key]) : undefined;

export const makeSaitamaBuckets = (dateRange, grain) => {
  const current = moment.tz(dateRange[0], "YYYY-MM-DD", "Asia/Tokyo");
  const end = moment.tz(dateRange[1], "YYYY-MM-DD", "Asia/Tokyo");
  if (grain === "hour") end.endOf("day");
  const buckets = [];
  while (current.isSameOrBefore(end, grain === "hour" ? "hour" : "day")) {
    buckets.push(grain === "hour" ? current.format("YYYY-MM-DD HH:00") : current.format("YYYY-MM-DD"));
    current.add(1, grain === "hour" ? "hour" : "day");
  }
  return buckets;
};

const indexRows = (rows, key, grain) => rows.reduce((acc, row) => {
  const bucket = String(row.bucket).slice(0, grain === "hour" ? 16 : 10);
  acc[bucket] = Number(row[key]);
  return acc;
}, {});

export const buildSaitamaViewModel = ({ responses, grain, dateRange }) => {
  const buckets = makeSaitamaBuckets(dateRange, grain);
  const assets = indexRows(normalizeRows(responses.chartAssets), "photographed_asset_count", grain);
  const photographers = indexRows(normalizeRows(responses.chartPhotographers), "unique_photographer_count", grain);
  return {
    date: buckets,
    countDenchu: buckets.map(bucket => assets[bucket] === undefined ? 0 : assets[bucket]),
    unique: buckets.map(bucket => photographers[bucket] === undefined ? 0 : photographers[bucket]),
    newUser: buckets.map(() => null),
  };
};

export const buildSaitamaOverview = ({ responses, grain, dateRange }) => {
  const approved = firstValue(normalizeRows(responses.kpi03), "review_ok_asset_count");
  const denominator = firstValue(normalizeRows(responses.kpi06Denominator), "registered_eligible_street_light_count");
  return {
    homeDataPhotoShoot: {
      countPic: firstValue(normalizeRows(responses.kpi01), "total_count"),
      countAsset: firstValue(normalizeRows(responses.kpi02), "total_asset_count"),
      countReviewOk: approved,
      countDenchu: firstValue(normalizeRows(responses.kpi02), "total_asset_count"),
      countUniqueUser: firstValue(normalizeRows(responses.kpi05), "unique_photographer_count"),
      progressDenchu: { taken: approved, max: denominator },
      achievementRate: denominator > 0 && approved !== undefined ? (approved / denominator) * 100 : undefined,
    },
    photoShootTrend: [buildSaitamaViewModel({ responses, grain, dateRange })],
  };
};
